import { useEffect, useRef, useCallback } from 'react'
import { AUTH_CONFIG } from './authConfig'

interface InactivityTimerConfig {
  timeout: number // timeout in milliseconds
  onTimeout: () => void
  enabled: boolean
}

/**
 * Custom hook to track user inactivity and trigger logout after specified timeout
 * Tracks mouse movement, keyboard input, clicks, scroll, and touch events
 * Supports multi-tab synchronization via localStorage
 */
export const useInactivityTimer = ({ timeout, onTimeout, enabled }: InactivityTimerConfig) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (!enabled) return

    // Clear existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
    }

    // Update last activity timestamp
    lastActivityRef.current = Date.now()
    
    // Store last activity in localStorage for multi-tab sync
    try {
      localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.LAST_ACTIVITY, lastActivityRef.current.toString())
    } catch (error) {
      console.error('Failed to update lastActivity in localStorage:', error)
    }

    // Set new timeout
    timeoutIdRef.current = setTimeout(() => {
      onTimeout()
    }, timeout)
  }, [timeout, onTimeout, enabled])

  // Activity event handler
  const handleActivity = useCallback(() => {
    resetTimer()
  }, [resetTimer])

  // Check for activity in other tabs
  const checkOtherTabsActivity = useCallback(() => {
    if (!enabled) return

    try {
      const storedLastActivity = localStorage.getItem(AUTH_CONFIG.STORAGE_KEYS.LAST_ACTIVITY)
      if (storedLastActivity) {
        const lastActivity = parseInt(storedLastActivity, 10)
        const timeSinceLastActivity = Date.now() - lastActivity

        // If activity detected in another tab within timeout period, reset timer
        if (timeSinceLastActivity < timeout && lastActivity > lastActivityRef.current) {
          lastActivityRef.current = lastActivity
          resetTimer()
        }
      }
    } catch (error) {
      console.error('Failed to check other tabs activity:', error)
    }
  }, [timeout, resetTimer, enabled])

  // Handle storage events (for multi-tab synchronization)
  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      // If logout happened in another tab
      if (event.key === AUTH_CONFIG.STORAGE_KEYS.IS_LOGGED_IN && event.newValue === 'false') {
        onTimeout()
      }
      // If activity happened in another tab
      else if (event.key === AUTH_CONFIG.STORAGE_KEYS.LAST_ACTIVITY) {
        checkOtherTabsActivity()
      }
    },
    [onTimeout, checkOtherTabsActivity]
  )

  useEffect(() => {
    if (!enabled) {
      // Clear timeout if disabled
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
        timeoutIdRef.current = null
      }
      return
    }

    // Initialize timer
    resetTimer()

    // Add event listeners for activity tracking
    AUTH_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    // Add storage event listener for multi-tab sync
    window.addEventListener('storage', handleStorageChange)

    // Check other tabs activity periodically
    const intervalId = setInterval(checkOtherTabsActivity, AUTH_CONFIG.SESSION_CHECK_INTERVAL)

    // Cleanup
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }
      AUTH_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity)
      })
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(intervalId)
    }
  }, [enabled, handleActivity, handleStorageChange, resetTimer, checkOtherTabsActivity])

  // Return method to manually reset timer (useful for API calls)
  return {
    resetTimer,
  }
}
